
import React, { useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import FileUpload from 'components/Inputs/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { TRANSACTION_TEMPLATE, downloadCSVTemplate, TemplateTooltipContent } from './UploadTemplates';
import Loader from 'components/Loader/Loader';
import { parseCSV } from 'helpers/parseFile';
import { API_URL } from '../../configs/Api';
import { getHeaders } from '../../middleware/Api';
const styles = require('./styles.pcss');

type Props = {
    enqueueSnackBarMessage: Function;
    organizationId: string;
};

export default function TransactionCSVUpload(props: Props) {
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [referenceId, setReferenceId] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleUpload = async () => {
        if (!csvFile) {
            props.enqueueSnackBarMessage('Must upload CSV file.', 'error');
            return;
        }
        setIsLoading(true);

        try {
            const parsedData = await parseCSV(csvFile);
            if (parsedData.length <= 0) {
                throw new Error('No data to upload');
            }

            const res = await (
                await fetch(API_URL + 'job/import-transactions-csv', {
                    method: 'post',
                    headers: getHeaders(),
                    body: JSON.stringify({
                        organizationId: props.organizationId,
                        referenceId: referenceId.trim() || null,
                        fileName: csvFile.name,
                        data: parsedData,
                        fileContent: await csvFile.text(),
                    }),
                })
            ).json();

            if (res.type === 'error') {
                const message =
                    res?.payload?.message || 'Transaction CSV upload failed.';

                props.enqueueSnackBarMessage('Error: ' + message, 'error');
                return;
            }

            if (res.type === 'data') {
                props.enqueueSnackBarMessage(
                    `${csvFile.name} was uploaded and transaction processing has started for ${props.organizationId}.`,
                    'success',
                );
                setCsvFile(null);
                setReferenceId('');
            }
        } catch (e: any) {
            const message = e?.message || 'Transaction CSV upload failed.';
            props.enqueueSnackBarMessage('Error: ' + message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadTemplate = () => downloadCSVTemplate(TRANSACTION_TEMPLATE);

    const templateTooltip = <TemplateTooltipContent template={TRANSACTION_TEMPLATE} />;

    const handleFileChange = (files: FileList) => {
        if (files[0]) {
            setCsvFile(files[0]);
        }
    };

    return (
        <Card className={styles.bulkCategoryUploadCard}>
            <CardHeader
                title="Transaction CSV Upload"
                className={styles.header}
            />
            <CardContent>
                <Typography
                    variant="body1"
                    className={styles.description}
                >
                    Upload the customer’s transactions CSV file to populate any missing transactions. Click the button below to start the process.
                </Typography>
                <Tooltip title={templateTooltip} arrow>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadTemplate}
                        style={{ marginBottom: 16 }}
                    >
                        Download Template
                    </Button>
                </Tooltip>
                <FileUpload
                    onChange={handleFileChange}
                    value={csvFile}
                    mimeType="text/csv"
                />

                <TextField
                    label="Reference ID"
                    placeholder="Optional (organizationId_DDMonthYYYY.csv)"
                    sx={{ width: '50%' }}
                    margin="normal"
                    value={referenceId}
                    onChange={(event) => setReferenceId(event.target.value)}
                />

                <Typography
                    variant="body2"
                    className={styles.description}
                >
                    Naming format: organizationId_DDMonthYYYY.csv
                </Typography>

                <div className={styles.buttonContainer}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUpload}
                        className={styles.button}
                        disabled={isLoading}
                    >
                        Upload Transaction CSV
                    </Button>
                </div>
            </CardContent>
            {isLoading && (
                <div className={styles.infoLoader}>
                    <Loader visible={true} />
                </div>
            )}
        </Card>
    );
}
